/**
 * Pagina de materiale MOS — grafice randate și rapoarte, servite static.
 *
 * Nu recalculăm nimic aici. Graficele vin gata făcute din lanțul operațional
 * al serviciului național, iar pagina doar le organizează: secțiune, poluant,
 * dată. Structura o ia din `manifest.json`, scris de build_mos_assets.py, ca
 * adăugarea unei date noi să nu ceară nicio modificare de cod.
 */
const BASE = import.meta.env.BASE_URL || '/cams-explorer/';
const MOS_BASE = `${BASE}data/mos/`;

let manifest = null;
const stare = { sectiune: null, poluant: null, data: null };

const el = id => document.getElementById(id);

function scapa(s) {
  return String(s).replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/** Data ISO → „24 septembrie 2026". */
function dataLizibila(iso) {
  const luni = ['ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie',
    'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie'];
  const [a, l, z] = iso.split('-').map(Number);
  return `${z} ${luni[l - 1]} ${a}`;
}

function randeazaSectiuni() {
  const chei = Object.keys(manifest.sectiuni);
  el('sectiuni').innerHTML = chei.map(k => `
    <button type="button" class="mos-tab${k === stare.sectiune ? ' activ' : ''}"
            data-sectiune="${k}" role="tab" aria-selected="${k === stare.sectiune}">
      ${scapa(manifest.sectiuni[k].titlu)}
    </button>`).join('');

  el('sectiuni').querySelectorAll('[data-sectiune]').forEach(b => {
    b.addEventListener('click', () => {
      stare.sectiune = b.dataset.sectiune;
      // Păstrăm poluantul dacă există și în secțiunea nouă; altfel primul.
      const pol = manifest.sectiuni[stare.sectiune].poluanti;
      if (!pol[stare.poluant]) stare.poluant = Object.keys(pol)[0];
      stare.data = null;
      randeazaTot();
    });
  });
}

function randeazaControale() {
  const s = manifest.sectiuni[stare.sectiune];
  const pol = s.poluanti;

  el('descriere').textContent = s.descriere;
  el('serii').innerHTML = s.serii.map(x =>
    `<span class="mos-serie">${scapa(x)}</span>`).join('');

  el('poluanti').innerHTML = Object.keys(pol).map(p => `
    <button type="button" class="mos-pol${p === stare.poluant ? ' activ' : ''}"
            data-poluant="${p}">${scapa(pol[p].eticheta)}</button>`).join('');
  el('poluanti').querySelectorAll('[data-poluant]').forEach(b => {
    b.addEventListener('click', () => {
      stare.poluant = b.dataset.poluant;
      stare.data = null;
      randeazaTot();
    });
  });

  const date = pol[stare.poluant].date;
  if (!stare.data || !date.some(d => d.data === stare.data)) {
    stare.data = date[0].data;   // cea mai recentă
  }
  el('date').innerHTML = date.map(d => `
    <button type="button" class="mos-data${d.data === stare.data ? ' activ' : ''}"
            data-data="${d.data}">${scapa(dataLizibila(d.data))}</button>`).join('');
  el('date').querySelectorAll('[data-data]').forEach(b => {
    b.addEventListener('click', () => { stare.data = b.dataset.data; randeazaTot(); });
  });
}

function randeazaGrafic() {
  const date = manifest.sectiuni[stare.sectiune].poluanti[stare.poluant].date;
  const intrare = date.find(d => d.data === stare.data);
  if (!intrare) return;

  const url = `${MOS_BASE}${stare.sectiune}/${intrare.fisier}`;
  const alt = `${manifest.sectiuni[stare.sectiune].titlu} — `
            + `${manifest.sectiuni[stare.sectiune].poluanti[stare.poluant].eticheta}, `
            + dataLizibila(stare.data);

  // Graficele au 1–2 MB; arătăm un indiciu până se încarcă, altfel pare blocat.
  el('grafic').innerHTML = `
    <div class="mos-incarcare" id="mos-incarcare">Se încarcă graficul…</div>
    <a href="${url}" target="_blank" rel="noopener" class="mos-imagine-link"
       title="Deschide imaginea la dimensiune completă">
      <img src="${url}" alt="${scapa(alt)}" id="mos-img" />
    </a>`;
  const img = el('mos-img');
  const gata = () => { const l = el('mos-incarcare'); if (l) l.remove(); };
  img.addEventListener('load', gata);
  img.addEventListener('error', () => {
    el('grafic').innerHTML =
      `<div class="mos-eroare">Graficul nu s-a putut încărca:<br><code>${scapa(intrare.fisier)}</code></div>`;
  });
  if (img.complete) gata();
}

function randeazaRapoarte() {
  if (!manifest.rapoarte.length) {
    el('rapoarte-grup').style.display = 'none';
    return;
  }
  el('rapoarte').innerHTML = manifest.rapoarte.map(r => `
    <a class="mos-raport" href="${MOS_BASE}rapoarte/${encodeURIComponent(r.fisier)}"
       target="_blank" rel="noopener">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14 2v6h6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="mos-raport-titlu">${scapa(r.titlu)}</span>
      <span class="mos-raport-marime">${r.marime_mb} MB · PDF</span>
    </a>`).join('');
}

function randeazaTot() {
  randeazaSectiuni();
  randeazaControale();
  randeazaGrafic();
}

async function init() {
  try {
    const r = await fetch(`${MOS_BASE}manifest.json`, { cache: 'no-cache' });
    if (!r.ok) throw new Error(`manifest.json → HTTP ${r.status}`);
    manifest = await r.json();
  } catch (e) {
    el('mos-continut').innerHTML = `
      <div class="mos-eroare">
        <strong>Materialele MOS nu sunt publicate pe acest server.</strong><br>
        Se generează cu <code>pipeline/build_mos_assets.py</code> și se
        sincronizează în <code>public/data/mos/</code>.<br>
        <span class="mos-eroare-detaliu">${scapa(e.message)}</span>
      </div>`;
    return;
  }

  const chei = Object.keys(manifest.sectiuni);
  if (!chei.length) {
    el('mos-continut').innerHTML =
      '<div class="mos-eroare">Manifestul nu conține nicio secțiune.</div>';
    return;
  }
  stare.sectiune = chei[0];
  stare.poluant = Object.keys(manifest.sectiuni[stare.sectiune].poluanti)[0];

  el('mos-gol').style.display = 'none';
  el('mos-corp').style.display = '';
  randeazaTot();
  randeazaRapoarte();

  if (manifest.generat) {
    el('generat').textContent =
      `Materiale actualizate: ${new Date(manifest.generat).toLocaleString('ro-RO')}`;
  }
}

init();
